import { useStore } from "@nanostores/react";
import { type UseTRPCQueryResult, type UseTRPCQuerySuccessResult } from "@trpc/react-query/shared";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ErrorPage } from "../components/shared/ErrorPage";
import { useAppContext, type AppContext } from "./ctx";
import { NotFoundPage } from "../components/shared/NotFound";
import { Loader } from "../components/shared/Loader";
import { lastNonAuthRouteVisitedStore } from "../components/shared/RouteNotAuth";

class CheckExistsError extends Error {};
const CheckExistsFn = <T,>(value: T, message?: string): NonNullable<T> => {
    if (!value) {
        throw new CheckExistsError(message)
    }
    return value
}

class CheckAccessError extends Error {};
const CheckAccessFn = <T,>(value: T, message?: string): NonNullable<T> => {
    if (!value) {
        throw new CheckAccessError(message)
    }
    return value
}

class GetAuthorizeMeError extends Error {};

type Props = Record<string, any>;
type QueryResult = UseTRPCQueryResult<any, any>;
type QuerySuccessResult<TQueryResult extends QueryResult> = UseTRPCQuerySuccessResult<NonNullable<TQueryResult['data']>, null>
type HelperProps<TQueryResult extends QueryResult | undefined> = {
    ctx: AppContext,
    queryResult: TQueryResult extends QueryResult ? QuerySuccessResult<TQueryResult> : undefined
}
type SetPropsProps<TQueryResult extends QueryResult | undefined> = HelperProps<TQueryResult> & {
    checkExists: typeof CheckExistsFn,
    checkAccess: typeof CheckAccessFn,
    getAuthorizeMe: (message?: string) => NonNullable<AppContext['me']>
}
type PageWrapperProps<TProps extends Props, TQueryResult extends QueryResult | undefined> = {
    redirectAuthorized?: boolean,

    authorizedOnly?: boolean,
    authorizedOnlyTitle?: string,
    authorizedOnlyMessage?: string,

    checkAccess?: (helperProps: HelperProps<TQueryResult>) => boolean,
    checkAccessTitle?: string,
    checkAccessMessage?: string,

    checkExists?: (helperProps: HelperProps<TQueryResult>) => boolean,
    checkExistsTitle?: string,
    checkExistsMessage?: string,

    showLoaderOnFetching?: boolean,

    useQuery?: () => TQueryResult,
    // this is when we didn't have the error classes
    // setProps?: (helperProps: HelperProps<TQueryResult>) => TProps,
    setProps?: (setPropsProps: SetPropsProps<TQueryResult>) => TProps 
    Page: React.FC<TProps>
}

const PageWrapper = <TProps extends Props = {}, TQueryResult extends QueryResult | undefined = undefined>({
    authorizedOnly,
    authorizedOnlyTitle = "Authorized Only",
    authorizedOnlyMessage = "This page is only for authorized users",
    redirectAuthorized,
    checkAccess,
    checkAccessTitle = "Access Denied",
    checkAccessMessage = "You have no access",
    checkExists,
    checkExistsTitle,
    checkExistsMessage,
    useQuery,
    setProps,
    Page,
    showLoaderOnFetching = true
}: PageWrapperProps<TProps, TQueryResult>) => {
    const nonAuthRouteLastVisited = useStore(lastNonAuthRouteVisitedStore);
    const navigate = useNavigate();
    const ctx = useAppContext();
    const queryResult = useQuery?.();

    const redirectNeeded = redirectAuthorized && ctx.me;

    useEffect(() => {
        if (redirectNeeded) {
            navigate(nonAuthRouteLastVisited, { replace: true })
        }
    }, [redirectNeeded, navigate, nonAuthRouteLastVisited])

    if (queryResult?.isLoading || (showLoaderOnFetching && queryResult?.isFetching) || redirectNeeded) {
        return <Loader type="page" />;
    }

    if (queryResult?.isError) {
        return <ErrorPage message={queryResult.error.message} />
    }

    if (authorizedOnly && !ctx.me) {
        return <ErrorPage title={authorizedOnlyTitle} message={authorizedOnlyMessage} />
    }

    const helperProps = { ctx, queryResult: queryResult as never }

    if (checkAccess) {
        const accessDenied = !checkAccess(helperProps);
        if (accessDenied) {
            return <ErrorPage title={checkAccessTitle} message={checkAccessMessage} />
        }
    }

    if (checkExists) {
        const notExists = !checkExists(helperProps);
        if (notExists) {
            return <NotFoundPage title={checkExistsTitle} message={checkExistsMessage} />
        }
    }

    const getAuthorizeMe = (message?: string) => {
        if (!ctx.me) {
            throw new GetAuthorizeMeError(message)
        }
        return ctx.me;
    }

    try {
        const props = setProps?.({ 
            ...helperProps,
            checkExists: CheckExistsFn, 
            checkAccess: CheckAccessFn, 
            getAuthorizeMe
        }) as TProps;
        return <Page {...props} />
    } catch (error) {
        if (error instanceof CheckExistsError) {
            return <ErrorPage title={checkExistsTitle} message={checkExistsMessage} />
        }
        if (error instanceof CheckAccessError) {
            return <ErrorPage title={checkAccessTitle} message={checkAccessMessage} />
        }
        if (error instanceof GetAuthorizeMeError) {
            return <ErrorPage title={authorizedOnlyTitle} message={authorizedOnlyMessage} />
        }
        throw error;
    }
    // const props = setProps?.(helperProps) as TProps;
    // return <Page {...props} />
}

// Call withWrapper on the page using PageWrapperProps as main type. Excludes 'Page' attribute by using Omit.
// Pass 'Page' in separately and return PageWrapper with Page inside
export const withPageWrapper = <TProps extends Props = {}, TQueryResult extends QueryResult | undefined = undefined>(
    pageWrapperProps: Omit<PageWrapperProps<TProps, TQueryResult>, 'Page'>
) => {
    return (Page: PageWrapperProps<TProps, TQueryResult>['Page']) => {
        return () => <PageWrapper {...pageWrapperProps} Page={Page} />
    }
}

